import { capitalize, getRequiredFields } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { schema } from "./validations";
import { toast } from "@/components/Toast";
import {
  useAddCashAppMutation,
  useAddPostCashAppMutation,
  useGetAttachmentById,
  useGetDocumentTypeDropdown,
  useGetGlMasterData,
  useGetParkedDocById,
  useGetPostedDocById,
} from "@/services";
import {
  AMOUNT,
  BANK_CONF,
  BANK_INFO,
  BILLING_MONTH,
  BILLING_MONTH_YEAR,
  BILLING_YEAR,
  CASH_APP_FILE,
  CASH_APP_ID,
  CHEQUE_NO,
  CLEARING,
  COMBINE,
  COMMENT,
  CURRENCY,
  CUSTOMER_ACC,
  DATE,
  DOCUMENT_ID,
  DOCUMENT_TYPE,
  DRCR,
  GL_ACCOUNT,
  LINE_ITEM_TEXT,
  LINE_ITEMS,
  OFFSET_ACC,
  OL_DATE,
  OL_DOC_LI,
  OL_REF,
  OL_TOTAL,
  PAYMENT_METHOD,
  POSTING_DATE,
  POSTING_MONTH,
  SUB_LEDGER,
  YEAR,
} from "./consts";
import moment from "moment";

const useAddEdit = () => {
  const { id, action, status } = useParams();
  const navigate = useNavigate();
  const cashAppFileRef = useRef(null);
  const isView = action == "view";
  const isPosted = status == "posted";
  const [keys, setKeys] = useState({
    accountName: Math.random() * 2,
    documentTypes: Math.random() * 2,
    paymentTerm: Math.random() * 2,
  });
  const [isEntryView, setIsEntryView] = useState<boolean>(true);
  const attachmentId = useRef<any>(null);
  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const form = useForm<any>({
    defaultValues: {
      [CASH_APP_ID]: "",
      [DOCUMENT_TYPE]: "",
      [BILLING_MONTH_YEAR]: null,
      [COMMENT]: "",
      // [ATTACHMENT_FILE]: null,
      [CASH_APP_FILE]: null,
      [POSTING_DATE]: "",
      [POSTING_MONTH]: "",
      [YEAR]: "",
      [LINE_ITEMS]: [],
    },
    resolver: zodResolver(schema),
  });

  const requiredFields = getRequiredFields(schema);

  // const formData = useWatch({control: form.control});
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const breadcrumbData = useMemo(() => {
    return [
      {
        link: `/${routes.admin}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityDetail?.name || ""}`,
      },
      {
        link: `/${routes.admin}/${routes.activityDashboard}/${routes.cashApp}`,
        name: <span>{"Cash App"}</span>,
      },
      {
        link: "",
        name: (
          <span className="text-primary">
            {id ? `${capitalize(action || "")} Cash App` : "Add Cash App"}
          </span>
        ),
      },
    ];
  }, []);

  const breadcrumbTitle = `Cash App - ${communityDetail?.name}`;

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.activityDashboard}/${routes.cashApp}`);
  };

  const { data: documentTypes, isLoading: isDocumentTypeLoading } =
    useGetDocumentTypeDropdown({
      communityId: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const { data: glData, isLoading: isLoadingGlMaster } = useGetGlMasterData({
    communityId: communityDetail?.communityId,
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  const { data: parkCashAppData, isLoading: isParkCashAppLoading } =
    useGetParkedDocById({
      communityId: communityDetail?.communityId as string,
      id: id as string,
      queryParams: {
        enabled: !!(id && communityDetail?.communityId && !isPosted),
      },
    });

  const { data: postCashAppData, isLoading: isPostCashAppLoading, refetch: refetchPostCashAppData } =
    useGetPostedDocById({
      communityId: communityDetail?.communityId as string,
      entryView: isEntryView,
      id: id as string,
      queryParams: {
        enabled: !!(id && communityDetail?.communityId && isPosted),
      },
    });

  const cashAppData = isPosted ? postCashAppData : parkCashAppData;
  const isCashAppLoading = isPosted
    ? isPostCashAppLoading
    : isParkCashAppLoading;

  useEffect(() => {
    if (isPosted) {
      refetchPostCashAppData();
    }
  }, [isEntryView]);

  useEffect(() => {
    if (cashAppData) {
      const header = cashAppData?.data?.header;
      attachmentId.current = header?.attachmentId;
      form.reset({
        [CASH_APP_ID]: isPosted
          ? header?.documentNumber?.toString() ||
            header?.[CASH_APP_ID]?.toString() ||
            ""
          : header?.parkDocumentHeaderId?.toString() ||
            header?.[CASH_APP_ID]?.toString() ||
            "",
        // [DOCUMENT_ID]: header?.[DOCUMENT_ID]?.toString() || "",
        [BILLING_MONTH_YEAR]:
          new Date(
            `${header?.postingPeriod?.toString()}/01/${header?.postingYear?.toString()}`
          ) || "",
        [COMMENT]: header?.comments?.toString() || "",
        // [ATTACHMENT_FILE]: {
        //   name:
        //     cashAppData?.data?.attachmentOriginalFileName?.toString() ||
        //     undefined,
        // },
        [CASH_APP_FILE]: {
          name: header?.cashAppOriginalFileName?.toString() || undefined,
        },
        [POSTING_DATE]: header?.[POSTING_DATE]?.toString() || "",
        [POSTING_MONTH]: header?.[POSTING_MONTH]?.toString() || "",
        [YEAR]: header?.[YEAR]?.toString() || "",
        [LINE_ITEMS]:
          cashAppData?.data?.[LINE_ITEMS]?.map((item: any) => {
            return {
              ...item,
              [DATE]: item?.[DATE]?.toString() || "",
              [CURRENCY]: item?.[CURRENCY]?.toString() || "USD",
              [BANK_INFO]: item?.[BANK_INFO]?.toString() || "",
              [BANK_CONF]: item?.[BANK_CONF]?.toString() || "",
              [DRCR]: item?.[DRCR]?.toString() || "",
              [LINE_ITEM_TEXT]: item?.[LINE_ITEM_TEXT]?.toString() || "",
              [CHEQUE_NO]: item?.[CHEQUE_NO]?.toString() || "",
              [AMOUNT]: item?.[AMOUNT]?.toString() || "",
              [GL_ACCOUNT]: item?.[GL_ACCOUNT]?.toString() || "",
              [SUB_LEDGER]: !!item?.[SUB_LEDGER],
              [COMBINE]: !!item?.[COMBINE],
              [CUSTOMER_ACC]: item?.[CUSTOMER_ACC]?.toString() || "",
              [PAYMENT_METHOD]: item?.[PAYMENT_METHOD]?.toString() || "",
              [OFFSET_ACC]: item?.[OFFSET_ACC]?.toString() || "",
              [CLEARING]: !!item?.[CLEARING],
              [OL_DATE]: item?.[OL_DATE]?.toString() || "",
              [OL_REF]: item?.[OL_REF]?.toString() || "",
              [OL_DOC_LI]: item?.[OL_DOC_LI]?.toString() || "",
              [OL_TOTAL]: item?.[OL_TOTAL]?.toString() || "",
            };
          }) || [],
      });
    }
  }, [cashAppData]);

  useEffect(() => {
    if (documentTypes) {
      const documentTypeId = documentTypes
        ?.find((data: any) => {
          return data?.documentType?.toLowerCase() == "oa";
        })
        ?.documentTypeId?.toString();
      if (documentTypeId) {
        form.setValue(DOCUMENT_ID, documentTypeId);
      } else {
        toast.error(
          "The Document Type 'OA' was not found. Kindly add it to proceed."
        );
        onBackClick();
      }
      setKeys((prev: any) => ({ ...prev, documentTypes: Math.random() * 4 }));
    }
  }, [documentTypes, cashAppData]);

  const {
    mutate: addInfo,
    isPending: isAddPending,
    isError: isAddCashAppError,
  } = useAddCashAppMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: (data: any) => {
      toast.success("Line items fetched Successfully");

      form.setValue(CASH_APP_ID, data?.data?.data?.[CASH_APP_ID]);
      const lineItems = data?.data?.data?.[LINE_ITEMS]?.map((item: any) => ({
        ...item,
        [DATE]: item?.[DATE]?.toString() || "",
        [CURRENCY]: item?.[CURRENCY]?.toString() || "USD",
        [BANK_INFO]: item?.[BANK_INFO]?.toString() || "",
        [BANK_CONF]: item?.[BANK_CONF]?.toString() || "",
        [DRCR]: item?.[DRCR]?.toString() || "",
        [LINE_ITEM_TEXT]: item?.[LINE_ITEM_TEXT]?.toString() || "",
        [CHEQUE_NO]: item?.[CHEQUE_NO]?.toString() || "",
        [AMOUNT]: item?.[AMOUNT]?.toString() || "",
        [GL_ACCOUNT]: item?.[GL_ACCOUNT]?.toString() || "",
        [SUB_LEDGER]: !!item?.[SUB_LEDGER],
        [COMBINE]: !!item?.[COMBINE],
        [CUSTOMER_ACC]: item?.[CUSTOMER_ACC]?.toString() || "",
        [PAYMENT_METHOD]: item?.[PAYMENT_METHOD]?.toString() || "",
        [OFFSET_ACC]: item?.[OFFSET_ACC]?.toString() || "",
        [CLEARING]: !!item?.[CLEARING],
        [OL_DATE]: item?.[OL_DATE]?.toString() || "",
        [OL_REF]: item?.[OL_REF]?.toString() || "",
        [OL_DOC_LI]: item?.[OL_DOC_LI]?.toString() || "",
        [OL_TOTAL]: item?.[OL_TOTAL]?.toString() || "",
      }));
      form.setValue(LINE_ITEMS, lineItems);
    },
  });

  const { mutate: postInfo, isPending: isPostPending } =
    useAddPostCashAppMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Cash App Document Posted Successfully");
        onBackClick();
      },
    });
    
  const onSubmit = (data: any) => {
    if (form.watch(LINE_ITEMS)?.length) {
      postInfo({
        communityId: communityDetail?.communityId as string,
        documentId: form.watch(CASH_APP_ID) as string,
      });
    } else {
      if (!data?.[BILLING_MONTH_YEAR]) {
        form.setError(BILLING_MONTH_YEAR, {
          message: "This field is required",
        });
        return;
      }
      if (!data?.[CASH_APP_FILE]) {
        form.setError(CASH_APP_FILE, {
          message: "This field is required",
        });
        return;
      }
      // if (!data?.[ATTACHMENT_FILE]) {
      //   form.setError(ATTACHMENT_FILE, {
      //     message: "This field is required",
      //   });
      //   return;
      // }
      if (!data?.[POSTING_DATE]) {
        form.setError(POSTING_DATE, {
          message: "This field is required",
        });
        return;
      }

      delete data[LINE_ITEMS];

      addInfo({
        data: {
          ...data,
          communityId: communityDetail?.communityId,
          [BILLING_MONTH]: data?.[BILLING_MONTH_YEAR]
            ? moment(data?.[BILLING_MONTH_YEAR]).format("MM")
            : "",
          [BILLING_YEAR]: data?.[BILLING_MONTH_YEAR]
            ? moment(data?.[BILLING_MONTH_YEAR]).format("YYYY")
            : "",
          [POSTING_DATE]: moment(data?.[POSTING_DATE]).format("YYYY-MM-DD"),
        },
      });
    }
  };

  const lineItems = useWatch({ control: form.control, name: LINE_ITEMS });

  const balanceAmount = useMemo(() => {
    if (Array.isArray(lineItems)) {
      const drValues = lineItems
        .filter((item) => item[DRCR]?.toLowerCase() == "dr" && item[AMOUNT])
        .reduce((sum, item) => sum + Number(item[AMOUNT] || 0), 0);

      const crValues = lineItems
        .filter((item) => item[DRCR]?.toLowerCase() == "dr" && item[AMOUNT])
        .reduce((sum, item) => sum + Number(item[AMOUNT] || 0), 0);

      return !isPosted ? Math.abs(drValues - crValues) : drValues - crValues;
    }
    return 0;
  }, [lineItems]);

  const { data: attachment, isRefetching: isAttachmentLoading, refetch: refetchAttachment } =
    useGetAttachmentById({
      id: attachmentId.current as string,
      queryParams: {
        enabled: false
      }
    });

  const onAttachmentClicked = () => {
    refetchAttachment();
  }

  useEffect(() => {
    if (attachment) {
      window.open(attachment?.data);
    }
  }, [attachment]);

  return {
    breadcrumbData,
    onBackClick,
    communityDetail,
    form,
    requiredFields,
    glData,
    isDocumentTypeLoading,
    documentTypes,
    isLoadingGlMaster,
    onCancel: onBackClick,
    breadcrumbTitle,
    keys,
    onSubmit,
    isView,
    isAddPending,
    isAddCashAppError,
    cashAppFileRef,
    balanceAmount,
    isPostPending,
    isCashAppLoading,
    isPosted,
    isEntryView,
    setIsEntryView,
    isAttachmentLoading,
    onAttachmentClicked
  };
};

export default useAddEdit;
